import { useState, useEffect } from "react";
import { Users, Plus, Search, Shield, Eye, EyeOff, Edit2, Trash2, UserCheck, UserX, Key, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  owner: "Owner",
  manager: "Manager",
  hr_finance: "HR & Finance",
  sales_team: "Sales Team",
  supervisor: "Supervisor",
  employee: "Employee"
};

const roleColors = {
  owner: "bg-purple-100 text-purple-700",
  manager: "bg-blue-100 text-blue-700",
  hr_finance: "bg-green-100 text-green-700",
  sales_team: "bg-amber-100 text-amber-700",
  supervisor: "bg-teal-100 text-teal-700",
  employee: "bg-slate-100 text-slate-700"
};

const sectionLabels = {
  dashboard: "Dashboard",
  sales: "Sales",
  crm: "CRM / Inquiries",
  products: "Products",
  inventory: "Inventory",
  attendance: "Attendance",
  employees: "Employees",
  financials: "Financials",
  analytics: "Analytics",
  production: "Production",
  payroll: "Payroll",
  settings: "Settings",
  customers: "Customers",
  user_management: "User Management"
};

export default function UserManagement() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    username: "", password: "", role: "employee", employee_id: ""
  });

  const [accessForm, setAccessForm] = useState({
    custom_access: []
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, employeesRes, sectionsRes] = await Promise.all([
        fetch(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/users/sections/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (employeesRes.ok) setEmployees(await employeesRes.json());
      if (sectionsRes.ok) {
        const data = await sectionsRes.json();
        setAllSections(data.sections || []);
      }
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.error("Username and password are required");
      return;
    }
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success("User created successfully");
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create user");
      }
    } catch (e) { toast.error("Failed to create user"); }
  };

  const handleUpdateAccess = async () => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${API}/api/users/${selectedUser.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ custom_access: accessForm.custom_access.length > 0 ? accessForm.custom_access : null })
      });
      if (res.ok) {
        toast.success("Access updated");
        setShowAccessModal(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update access");
      }
    } catch (e) { toast.error("Failed to update access"); }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await fetch(`${API}/api/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !user.is_active })
      });
      if (res.ok) {
        toast.success(user.is_active ? "User deactivated" : "User activated");
        fetchData();
      }
    } catch (e) { toast.error("Failed to update user"); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/api/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("User deleted");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch (e) { toast.error("Failed to delete user"); }
  };

  const openAccessModal = (user) => {
    setSelectedUser(user);
    setAccessForm({
      custom_access: user.custom_access || []
    });
    setShowAccessModal(true);
  };

  const toggleSection = (section) => {
    setAccessForm(prev => ({
      ...prev,
      custom_access: prev.custom_access.includes(section)
        ? prev.custom_access.filter(s => s !== section)
        : [...prev.custom_access, section]
    }));
  };

  const resetForm = () => {
    setForm({ username: "", password: "", role: "employee", employee_id: "" });
    setShowPassword(false);
  };

  // Get employees without user accounts
  const employeesWithoutAccounts = employees.filter(
    emp => !users.some(u => u.employee_id === emp.id)
  );

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.employee?.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-forest/20 border-t-forest rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-navy">User Management</h1>
          <p className="text-slate-500 text-sm font-sans">Manage portal access for employees</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-forest hover:bg-forest/90" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Create User
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search by username or employee name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-users" />
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{users.length}</p>
              <p className="text-sm text-slate-500">Total Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{users.filter(u => u.is_active).length}</p>
              <p className="text-sm text-slate-500">Active Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <UserX className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{employeesWithoutAccounts.length}</p>
              <p className="text-sm text-slate-500">Employees Without Access</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(user => (
          <Card key={user.id} className={`hover:shadow-md transition-shadow ${!user.is_active ? "opacity-60" : ""}`} data-testid={`user-card-${user.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${user.is_active ? "bg-forest/10" : "bg-slate-100"}`}>
                    <Shield className={`w-5 h-5 ${user.is_active ? "text-forest" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy font-sans">{user.username}</h3>
                    {user.employee && <p className="text-sm text-slate-500">{user.employee.name}</p>}
                  </div>
                </div>
                <Badge className={roleColors[user.role]}>{roleLabels[user.role]}</Badge>
              </div>

              <div className="mb-4">
                <p className="text-xs text-slate-400 mb-1">Access Sections:</p>
                <div className="flex flex-wrap gap-1">
                  {(user.effective_access || []).slice(0, 4).map(section => (
                    <Badge key={section} variant="outline" className="text-xs">{sectionLabels[section] || section}</Badge>
                  ))}
                  {(user.effective_access || []).length > 4 && (
                    <Badge variant="outline" className="text-xs">+{user.effective_access.length - 4} more</Badge>
                  )}
                </div>
                {user.custom_access && (
                  <p className="text-xs text-amber-600 mt-1">Custom access configured</p>
                )}
              </div>

              <div className="flex items-center justify-between pt-3 border-t">
                <Badge variant={user.is_active ? "default" : "secondary"} className={user.is_active ? "bg-green-100 text-green-700" : ""}>
                  {user.is_active ? "Active" : "Inactive"}
                </Badge>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openAccessModal(user)} data-testid={`edit-access-${user.id}`}>
                    <Key className="w-3.5 h-3.5 mr-1" /> Access
                  </Button>
                  {user.role !== "owner" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleToggleActive(user)} data-testid={`toggle-user-${user.id}`}>
                        {user.is_active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {user.last_login && (
                <p className="text-xs text-slate-400 mt-3">Last login: {new Date(user.last_login).toLocaleString()}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card><CardContent className="py-12 text-center text-slate-500">
          {search ? "No users match your search" : "No users found"}
        </CardContent></Card>
      )}

      {/* Create User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Portal User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label>Username (Email) *</Label>
              <Input type="email" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="employee@company.com" required data-testid="input-username" />
            </div>
            <div>
              <Label>Password *</Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Create a strong password" required data-testid="input-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="hr_finance">HR & Finance</SelectItem>
                  <SelectItem value="sales_team">Sales Team</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to Employee (Optional)</Label>
              <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="select-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No employee link</SelectItem>
                  {employeesWithoutAccounts.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.department})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Only employees without portal access are shown</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-user">Create User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Access Modal */}
      <Dialog open={showAccessModal} onOpenChange={setShowAccessModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Access - {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">
                <strong>Role:</strong> {roleLabels[selectedUser?.role]}
              </p>
              <p className="text-xs text-slate-500">
                {accessForm.custom_access.length > 0 
                  ? "Custom access is enabled. Uncheck all to use role-based defaults."
                  : "Using default role-based access. Select sections to customize."}
              </p>
            </div>

            <div>
              <Label className="mb-3 block">Section Access</Label>
              <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                {allSections.filter(s => s !== "user_management").map(section => (
                  <div key={section} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-slate-50">
                    <Checkbox 
                      id={`section-${section}`} 
                      checked={accessForm.custom_access.includes(section)}
                      onCheckedChange={() => toggleSection(section)}
                      data-testid={`checkbox-${section}`}
                    />
                    <label htmlFor={`section-${section}`} className="text-sm font-medium cursor-pointer flex-1">
                      {sectionLabels[section] || section}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setAccessForm({ custom_access: allSections.filter(s => s !== "user_management") })}>
                Select All
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setAccessForm({ custom_access: [] })}>
                Clear All (Use Defaults)
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAccessModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateAccess} className="bg-forest hover:bg-forest/90" data-testid="save-access">
              <Check className="w-4 h-4 mr-1" /> Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
